import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { ButtonLink } from 'stile-shared/src/components/2020/common/ButtonLink';
import { P } from 'stile-shared/src/components/2020/common/Primitives';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

function ICTPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/ict_professionals_cover.png"
            alt=""
          ></StaticImage>
        }
      />

      <TextBlock>
        <P>
          We believe your data is exactly that—yours. We do not claim any right to it. We have never
          sold school data to third parties and never will. We do not track students (or teachers!)
          for advertising purposes. But it is our responsibility to help you keep your data safe.
          We’re constantly monitoring and testing Stile’s security and are regularly audited against
          industry best practices.
        </P>
      </TextBlock>

      <TextBlock title="Independently audited security and privacy practices">
        <P>
          Our security and privacy processes are audited for compliance against the international,
          best-practice SOC2 standard. We also undergo yearly penetration testing of our computer
          systems, where “friendly hackers” attempt to break into our systems. We make the results
          from this testing available to customers on request.
        </P>
      </TextBlock>

      <TeaserBlock
        title="EdTech Security & Privacy Checklist"
        variant="beige"
        contentSide="right"
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/ed_tech_resource.png"
            alt=""
          />
        }
      >
        <P>Follow this checklist guide to ensure your students’ data is safe.</P>
        <ButtonLink url="/resources/security-privacy-checklist/">Download now →</ButtonLink>
      </TeaserBlock>

      <TextBlock title="Trusted by departments around the country">
        <P>
          We’ve partnered with most state and territory governments to help teachers deliver safe
          and secure science education. This includes everything from rigorous vetting to
          integrating with their login systems to add a further layer of security for students and
          teachers. We also proudly support Safer Technologies 4 Schools (ST4S), a government
          initiative that evaluates digital products and services used by schools across Australia
          against a nationally consistent security and privacy control framework. We work closely
          with ST4S to push for further privacy and security standards in the education technology
          sector.
        </P>
      </TextBlock>

      <TextBlock title="Australian-owned and operated">
        <P>
          As an Australian company, we’re bound by and fully comply with the Privacy Act 1988 and
          the Australian Privacy Principles. All of the data we hold on your behalf is securely
          stored in Australian data centres.
        </P>
      </TextBlock>

      <TextBlock title="Monitored 24/7">
        <P>
          Our specialist IT operations team constantly monitors Stile’s systems and is on call 24/7
          to respond should the need arise. All Stile staff, including the operations team, receive
          regular training on security, privacy, and the appropriate handling of school data. All
          staff access to your data is logged and routinely audited.
        </P>
      </TextBlock>

      <TextBlock title="Seamless integrations">
        <P>
          Single Sign-On (SSO) makes accessing Stile more efficient and secure for teachers and
          students by using your school’s email credentials. SSO integrations with Google,
          Microsoft, Cloudwork, and other SAML Identity Providers are included in Stile
          subscriptions to streamline your digital tools and resources. We also offer Sign in with
          Microsoft as an alternative to SSO. Many larger integrations have already been established
          with state governments around the country. Stile also partners with RiskAssess, ClickView,
          and Canvas LMS.
        </P>
      </TextBlock>

      <NextPageBlock path="/resources/parents-and-guardians/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default ICTPage;
